import { Link } from 'react-router-dom'
import { TrendingUp, Users, Globe, Search, ShoppingCart, BarChart3, CheckCircle } from 'lucide-react'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useCart, ConsultingPackage } from '../contexts/CartContext'
import { useLanguage } from '../contexts/LanguageContext'
import CartModal from '../components/CartModal'
import { trackMetaAddToCart } from '../utils/analytics'

const Consulting = () => {
  const { addToCart } = useCart()
  const { language } = useLanguage()
  const [isCartOpen, setIsCartOpen] = useState(false)

  const consultingPackages: ConsultingPackage[] = [
    {
      id: 'google-ads-consulting',
      name: 'Google Ads Danışmanlığı',
      nameEn: 'Google Ads Consulting',
      nameRu: 'Консалтинг Google Ads',
      description: 'Google Ads kampanyalarınızı optimize edin ve ROI\'nizi artırın. Uzman danışmanlarımızla 1 saatlik özel danışmanlık.',
      descriptionEn: 'Optimize your Google Ads campaigns and increase your ROI. 1-hour private consultation with our experts.',
      descriptionRu: 'Оптимизируйте свои кампании Google Ads и увеличивайте ROI. Частная консультация 1 час с нашими экспертами.',
      pricePerHour: 3000,
      hours: 1,
      totalPrice: 3000,
      features: [
        'Kampanya stratejisi belirleme',
        'Anahtar kelime analizi',
        'ROI optimizasyonu',
        'Reklam metni önerileri',
        'Performans raporlama'
      ],
      featuresEn: [
        'Campaign strategy development',
        'Keyword analysis',
        'ROI optimization',
        'Ad copy suggestions',
        'Performance reporting'
      ],
      featuresRu: [
        'Разработка стратегии кампании',
        'Анализ ключевых слов',
        'Оптимизация ROI',
        'Предложения по рекламным текстам',
        'Отчетность о производительности'
      ],
      category: 'Reklam Yönetimi',
      categoryEn: 'Ad Management',
      categoryRu: 'Управление рекламой',
      icon: 'TrendingUp'
    },
    {
      id: 'meta-ads-consulting',
      name: 'Meta Ads Danışmanlığı',
      nameEn: 'Meta Ads Consulting',
      nameRu: 'Консалтинг Meta Ads',
      description: 'Facebook ve Instagram reklamlarınızı en verimli şekilde yönetin. Sosyal medya reklam stratejileri ve optimizasyon.',
      descriptionEn: 'Manage your Facebook and Instagram ads most efficiently. Social media ad strategies and optimization.',
      descriptionRu: 'Управляйте своей рекламой в Facebook и Instagram наиболее эффективно. Стратегии рекламы в социальных сетях и оптимизация.',
      pricePerHour: 3000,
      hours: 1,
      totalPrice: 3000,
      features: [
        'Hedef kitle analizi',
        'Yaratıcı stratejileri',
        'Bütçe optimizasyonu',
        'Dönüşüm takibi',
        'A/B test önerileri'
      ],
      featuresEn: [
        'Audience analysis',
        'Creative strategies',
        'Budget optimization',
        'Conversion tracking',
        'A/B test suggestions'
      ],
      featuresRu: [
        'Анализ целевой аудитории',
        'Креативные стратегии',
        'Оптимизация бюджета',
        'Отслеживание конверсий',
        'Предложения по A/B тестам'
      ],
      category: 'Reklam Yönetimi',
      categoryEn: 'Ad Management',
      categoryRu: 'Управление рекламой',
      icon: 'Users'
    },
    {
      id: 'seo-consulting',
      name: 'SEO Danışmanlığı',
      nameEn: 'SEO Consulting',
      nameRu: 'SEO Консалтинг',
      description: 'Arama motorlarında üst sıralarda yer alın. SEO stratejisi, teknik optimizasyon ve içerik planlama.',
      descriptionEn: 'Rank higher in search engines. SEO strategy, technical optimization and content planning.',
      descriptionRu: 'Занимайте верхние позиции в поисковых системах. SEO-стратегия, техническая оптимизация и планирование контента.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'SEO audit ve analiz',
        'Teknik SEO optimizasyonu',
        'İçerik stratejisi',
        'Anahtar kelime araştırması',
        'Backlink stratejisi'
      ],
      featuresEn: [
        'SEO audit and analysis',
        'Technical SEO optimization',
        'Content strategy',
        'Keyword research',
        'Backlink strategy'
      ],
      featuresRu: [
        'SEO аудит и анализ',
        'Техническая SEO оптимизация',
        'Контент-стратегия',
        'Исследование ключевых слов',
        'Backlink стратегия'
      ],
      category: 'SEO & GEO',
      categoryEn: 'SEO & GEO',
      categoryRu: 'SEO & GEO',
      icon: 'Search'
    },
    {
      id: 'web-design-consulting',
      name: 'Web Tasarım Danışmanlığı',
      nameEn: 'Web Design Consulting',
      nameRu: 'Консалтинг веб-дизайна',
      description: 'Modern ve kullanıcı dostu web siteleri için tasarım danışmanlığı. UX/UI optimizasyonu ve dönüşüm artırma.',
      descriptionEn: 'Design consulting for modern and user-friendly websites. UX/UI optimization and conversion improvement.',
      descriptionRu: 'Консалтинг по дизайну для современных и удобных веб-сайтов. Оптимизация UX/UI и увеличение конверсии.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'UX/UI değerlendirme',
        'Dönüşüm optimizasyonu',
        'Responsive tasarım önerileri',
        'Kullanıcı deneyimi analizi',
        'Tasarım best practices'
      ],
      featuresEn: [
        'UX/UI evaluation',
        'Conversion optimization',
        'Responsive design suggestions',
        'User experience analysis',
        'Design best practices'
      ],
      featuresRu: [
        'Оценка UX/UI',
        'Оптимизация конверсии',
        'Предложения по адаптивному дизайну',
        'Анализ пользовательского опыта',
        'Лучшие практики дизайна'
      ],
      category: 'Web Tasarımı',
      categoryEn: 'Web Design',
      categoryRu: 'Веб-дизайн',
      icon: 'Globe'
    },
    {
      id: 'ecommerce-consulting',
      name: 'E-Ticaret Danışmanlığı',
      nameEn: 'E-commerce Consulting',
      nameRu: 'Консалтинг электронной коммерции',
      description: 'E-ticaret işinizi büyütün. Satış stratejileri, platform optimizasyonu ve müşteri deneyimi iyileştirme.',
      descriptionEn: 'Grow your e-commerce business. Sales strategies, platform optimization and customer experience improvement.',
      descriptionRu: 'Развивайте свой бизнес электронной коммерции. Стратегии продаж, оптимизация платформы и улучшение клиентского опыта.',
      pricePerHour: 3000,
      hours: 1,
      totalPrice: 3000,
      features: [
        'E-ticaret stratejisi',
        'Ürün katalog optimizasyonu',
        'Checkout süreci iyileştirme',
        'Müşteri segmentasyonu',
        'Pazarlama otomasyonu'
      ],
      featuresEn: [
        'E-commerce strategy',
        'Product catalog optimization',
        'Checkout process improvement',
        'Customer segmentation',
        'Marketing automation'
      ],
      featuresRu: [
        'Стратегия электронной коммерции',
        'Оптимизация каталога продуктов',
        'Улучшение процесса оформления заказа',
        'Сегментация клиентов',
        'Автоматизация маркетинга'
      ],
      category: 'E-Ticaret',
      categoryEn: 'E-commerce',
      categoryRu: 'Электронная коммерция',
      icon: 'ShoppingCart'
    },
    {
      id: 'social-media-consulting',
      name: 'Sosyal Medya Danışmanlığı',
      nameEn: 'Social Media Consulting',
      nameRu: 'Консалтинг социальных сетей',
      description: 'Sosyal medya stratejinizi güçlendirin. İçerik planlama, topluluk yönetimi ve marka bilinirliği artırma.',
      descriptionEn: 'Strengthen your social media strategy. Content planning, community management and brand awareness increase.',
      descriptionRu: 'Усильте свою стратегию в социальных сетях. Планирование контента, управление сообществом и повышение узнаваемости бренда.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'İçerik stratejisi',
        'Topluluk yönetimi',
        'Influencer işbirlikleri',
        'Sosyal medya analitiği',
        'Kriz yönetimi'
      ],
      featuresEn: [
        'Content strategy',
        'Community management',
        'Influencer collaborations',
        'Social media analytics',
        'Crisis management'
      ],
      featuresRu: [
        'Контент-стратегия',
        'Управление сообществом',
        'Сотрудничество с инфлюенсерами',
        'Аналитика социальных сетей',
        'Управление кризисами'
      ],
      category: 'Sosyal Medya',
      categoryEn: 'Social Media',
      categoryRu: 'Социальные сети',
      icon: 'Users'
    },
    {
      id: 'analytics-consulting',
      name: 'Analitik & Raporlama Danışmanlığı',
      nameEn: 'Analytics & Reporting Consulting',
      nameRu: 'Консалтинг аналитики и отчетности',
      description: 'Veri odaklı kararlar alın. Google Analytics, veri analizi ve performans raporlama danışmanlığı.',
      descriptionEn: 'Make data-driven decisions. Google Analytics, data analysis and performance reporting consulting.',
      descriptionRu: 'Принимайте решения на основе данных. Google Analytics, анализ данных и консалтинг по отчетности производительности.',
      pricePerHour: 4000,
      hours: 1,
      totalPrice: 4000,
      features: [
        'Google Analytics kurulumu',
        'Veri analizi ve yorumlama',
        'Dashboard oluşturma',
        'Performans metrikleri',
        'Raporlama stratejileri'
      ],
      featuresEn: [
        'Google Analytics setup',
        'Data analysis and interpretation',
        'Dashboard creation',
        'Performance metrics',
        'Reporting strategies'
      ],
      featuresRu: [
        'Настройка Google Analytics',
        'Анализ и интерпретация данных',
        'Создание дашбордов',
        'Метрики производительности',
        'Стратегии отчетности'
      ],
      category: 'Analitik',
      categoryEn: 'Analytics',
      categoryRu: 'Аналитика',
      icon: 'BarChart3'
    },
    {
      id: 'general-marketing-consulting',
      name: 'Genel Dijital Pazarlama Danışmanlığı',
      nameEn: 'General Digital Marketing Consulting',
      nameRu: 'Общий консалтинг цифрового маркетинга',
      description: 'Dijital pazarlama stratejinizi tüm yönleriyle değerlendirin. Kapsamlı pazarlama danışmanlığı ve yol haritası.',
      descriptionEn: 'Evaluate your digital marketing strategy comprehensively. Comprehensive marketing consulting and roadmap.',
      descriptionRu: 'Комплексно оцените свою стратегию цифрового маркетинга. Всесторонний маркетинговый консалтинг и дорожная карта.',
      pricePerHour: 3500,
      hours: 1,
      totalPrice: 3500,
      features: [
        'Pazarlama stratejisi',
        'Kanal analizi',
        'Bütçe planlama',
        'ROI optimizasyonu',
        'Kapsamlı raporlama'
      ],
      featuresEn: [
        'Marketing strategy',
        'Channel analysis',
        'Budget planning',
        'ROI optimization',
        'Comprehensive reporting'
      ],
      featuresRu: [
        'Маркетинговая стратегия',
        'Анализ каналов',
        'Планирование бюджета',
        'Оптимизация ROI',
        'Комплексная отчетность'
      ],
      category: 'Genel',
      categoryEn: 'General',
      categoryRu: 'Общее',
      icon: 'TrendingUp'
    }
  ]

  const getIcon = (iconName: string) => {
    const icons: Record<string, any> = {
      TrendingUp,
      Users,
      Search,
      Globe,
      ShoppingCart,
      BarChart3
    }
    return icons[iconName] || TrendingUp
  }

  const handleAddToCart = (packageItem: ConsultingPackage) => {
    addToCart(packageItem, 1)
    setIsCartOpen(true)
    
    // Track Meta Pixel AddToCart event
    trackMetaAddToCart({
      content_name: language === 'tr' ? packageItem.name : language === 'en' ? packageItem.nameEn : packageItem.nameRu || packageItem.nameEn,
      content_ids: [packageItem.id],
      content_type: 'consulting',
      value: packageItem.totalPrice,
    })
  }

  return (
    <>
      <Helmet>
        <title>{language === 'tr' ? 'Saatlik Danışmanlık - Thinklyze' : language === 'en' ? 'Hourly Consulting - Thinklyze' : 'Почасовая консультация - Thinklyze'}</title>
        <meta name="description" content={language === 'tr' ? 'Uzman dijital pazarlama danışmanlığı hizmetleri. Google Ads, SEO, Web Tasarım ve daha fazlası için saatlik danışmanlık paketleri.' : language === 'en' ? 'Expert digital marketing consulting services. Hourly consulting packages for Google Ads, SEO, Web Design and more.' : 'Экспертные услуги консалтинга в цифровом маркетинге. Почасовые пакеты консалтинга для Google Ads, SEO, веб-дизайна и многого другого.'} />
      </Helmet>

      <div className="pt-16 bg-[#f9f9f9] relative">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#e5e7eb_1px,transparent_1px),linear-gradient(to_bottom,#e5e7eb_1px,transparent_1px)] bg-[size:80px_80px] [mask-image:radial-gradient(ellipse_60%_40%_at_50%_10%,#000_20%,transparent_70%)] opacity-40 pointer-events-none"></div>
        
        {/* Hero Section */}
        <section className="py-24 relative">
          <div className="max-w-[1200px] mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="inline-block text-[13px] font-medium text-[#666] bg-white px-4 py-1.5 rounded-full border border-gray-200 mb-6">
                {language === 'tr' ? 'Saatlik Danışmanlık' : language === 'en' ? 'Hourly Consulting' : 'Почасовая консультация'}
              </span>
              <h1 className="text-[28px] md:text-[44px] font-bold text-[#111] leading-tight mb-6">
                {language === 'tr' 
                  ? 'Uzman Dijital Pazarlama Danışmanlığı'
                  : language === 'en' ? 'Expert Digital Marketing Consulting' : 'Экспертный консалтинг цифрового маркетинга'
                }
              </h1>
              <p className="text-lg md:text-xl text-[#555] max-w-3xl mx-auto mb-8 leading-relaxed">
                {language === 'tr'
                  ? 'Dijital pazarlama uzmanlarımızla 1 saatlik özel danışmanlık seansları. İhtiyacınıza özel çözümler ve stratejiler.'
                  : language === 'en' ? '1-hour private consultation sessions with our digital marketing experts. Custom solutions and strategies for your needs.' : 'Частные консультации на 1 час с нашими экспертами по цифровому маркетингу. Индивидуальные решения и стратегии для ваших потребностей.'
                }
              </p>
            </motion.div>
          </div>
        </section>

        {/* Packages Grid */}
        <section className="py-24 relative">
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {consultingPackages.map((packageItem, index) => {
                const IconComponent = getIcon(packageItem.icon || 'TrendingUp')
                return (
                  <motion.div
                    key={packageItem.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      to={`/consulting/${packageItem.id}`}
                      className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-300 h-full flex flex-col relative overflow-hidden"
                    >
                      {/* Gradient background on hover */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                      
                      <div className="relative z-10 flex flex-col h-full">
                        <div className="flex items-start justify-between mb-6">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg group-hover:shadow-xl">
                            <IconComponent className="w-7 h-7 text-white" />
                          </div>
                          <div className="text-right">
                            <div className="text-2xl font-bold text-gray-900">
                              {packageItem.totalPrice.toLocaleString('tr-TR')} ₺
                            </div>
                            <div className="text-sm text-gray-500">
                              {language === 'tr' ? 'saatlik' : language === 'en' ? 'per hour' : 'в час'}
                            </div>
                          </div>
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                          {language === 'tr' ? packageItem.name : language === 'en' ? packageItem.nameEn : (packageItem as any).nameRu}
                        </h3>
                        <p className="text-gray-600 mb-6 text-[15px] leading-relaxed flex-grow">
                          {language === 'tr' ? packageItem.description : language === 'en' ? packageItem.descriptionEn : (packageItem as any).descriptionRu}
                        </p>

                        <div className="space-y-2.5 mb-6">
                          {(language === 'tr' ? packageItem.features : language === 'en' ? packageItem.featuresEn : (packageItem as any).featuresRu).slice(0, 3).map((feature: string, idx: number) => (
                            <div key={idx} className="flex items-center gap-2.5 text-sm text-gray-700">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-3 mt-auto pt-4">
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              handleAddToCart(packageItem)
                            }}
                            className="flex-1 px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium"
                          >
                            {language === 'tr' ? 'Sepete Ekle' : language === 'en' ? 'Add to Cart' : 'Добавить в корзину'}
                          </button>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>
      </div>

      {/* Cart Modal */}
      <CartModal 
        isOpen={isCartOpen} 
        onClose={() => setIsCartOpen(false)}
      />
    </>
  )
}

export default Consulting















